import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { PageHeader } from "../components/TaskTable";
import TaskDrawer from "../components/TaskDrawer";
import { useData } from "../lib/DataContext";
import { PriorityBadge } from "../lib/ui";
import { AlertOctagon, Building2, Activity } from "lucide-react";

export default function CommandCenter() {
  const [data, setData] = useState(null);
  const [openId, setOpenId] = useState(null);
  const { userName, deptName } = useData();
  const nav = useNavigate();
  useEffect(() => { api.get("/command-center").then((r) => setData(r.data)); }, []);
  if (!data) return <div className="p-6 text-slate-400">Loading…</div>;
  const h = data.org_health;
  const scoreCards = [
    ["Open", h.total_open, "#2563EB"], ["Completed", h.completed, "#16A34A"], ["Overdue", h.overdue, "#C2410C"],
    ["Blocked", h.blocked, "#334155"], ["Critical", h.critical, "#DC2626"], ["Pending Approval", h.pending_approval, "#1E40AF"],
  ];

  return (
    <div className="p-6">
      <PageHeader title="Management Command Center" subtitle="Exception-driven organizational execution" />

      <div className="bg-white border rounded-xl p-4 mb-5">
        <div className="flex items-center gap-2 mb-3"><Activity size={16} className="text-blue-600" /><span className="text-sm font-semibold text-slate-700">Organization Health</span></div>
        <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
          {scoreCards.map(([l, v, c]) => (
            <div key={l} className="text-center">
              <div className="text-2xl font-bold font-mono" style={{ color: c }}>{v}</div>
              <div className="text-xs text-slate-500">{l}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="bg-white border rounded-xl p-4">
          <div className="flex items-center gap-2 mb-3"><AlertOctagon size={16} className="text-red-600" /><span className="text-sm font-semibold text-slate-700">Management Attention ({data.attention.length})</span></div>
          <div className="space-y-2 max-h-[520px] overflow-y-auto">
            {data.attention.length === 0 && <div className="text-sm text-slate-400 py-6 text-center">Nothing needs attention. 🎉</div>}
            {data.attention.map((t) => (
              <button key={t.id} data-testid="command-center-attention-card" onClick={() => setOpenId(t.id)}
                className="w-full text-left border rounded-lg p-3 hover:bg-red-50/40 transition-colors">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-[10px] text-slate-400">{t.human_id}</span>
                  <PriorityBadge value={t.priority} />
                  {t.days_overdue > 0 && <span className="text-xs text-red-600 font-medium">{t.days_overdue}d overdue</span>}
                </div>
                <div className="text-sm font-medium text-slate-800 mt-1">{t.title}</div>
                <div className="flex gap-1.5 mt-1.5 flex-wrap">
                  {t.reasons.map((r) => <span key={r} className="text-[10px] bg-red-100 text-red-700 rounded-full px-2 py-0.5">{r}</span>)}
                </div>
              </button>
            ))}
          </div>
        </div>

        <div className="bg-white border rounded-xl p-4">
          <div className="flex items-center gap-2 mb-3"><Building2 size={16} className="text-slate-600" /><span className="text-sm font-semibold text-slate-700">Department Bottlenecks</span></div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left border-b text-xs uppercase tracking-wide text-slate-400">
                <th className="py-2">Dept</th><th className="py-2">Open</th><th className="py-2">Overdue</th><th className="py-2">Blocked</th><th className="py-2">On-Time</th><th className="py-2">Pending In</th>
              </tr></thead>
              <tbody>
                {data.departments.filter((d) => d.open > 0 || d.completed > 0).map((d) => (
                  <tr key={d.id} className="border-b last:border-0 hover:bg-slate-50 cursor-pointer" onClick={() => nav("/departments")}>
                    <td className="py-2 font-medium text-slate-700">{d.name}</td>
                    <td className="py-2">{d.open}</td>
                    <td className="py-2 text-orange-600">{d.overdue}</td>
                    <td className="py-2 text-slate-600">{d.blocked}</td>
                    <td className="py-2">{d.on_time_pct}%</td>
                    <td className="py-2">{d.pending_from_others}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      {openId && <TaskDrawer taskId={openId} onClose={() => setOpenId(null)} onChanged={() => api.get("/command-center").then((r) => setData(r.data))} />}
    </div>
  );
}
